import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Sparkles, FileText, BarChart3, ClipboardCheck, ScrollText, Trash2 } from "lucide-react";
import Markdown from "./Markdown";
import { tr } from "@/lib/i18n";

const SUGGESTIONS = [
    { id: "summarize", label: "Summarize circulations", icon: FileText, prompt: "Summarize all circulation responses for this application. Structure it as: Overall Position, Key Concerns by Agency, Suggested Conditions, and Next Steps." },
    { id: "compliance", label: "Compliance check", icon: ClipboardCheck, prompt: "Run a Planning Act compliance check on this application's timeline. Flag any approaching deadlines based on the events." },
    { id: "report", label: "Draft council report", icon: ScrollText, prompt: "Draft a skeleton recommendation report for council for this application, with sections for Background, Proposal, Analysis, and Recommendation." },
    { id: "askdata", label: "Ask the data", icon: BarChart3, prompt: "Give me a snapshot of development activity: applications by status and type, total proposed units, and revenue collected vs outstanding." },
];

export default function AIChatPanel({ open, onClose, contextType = "general", contextId = null, preset, onPresetConsumed }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [streaming, setStreaming] = useState(false);
    const bottomRef = useRef(null);
    const sessionId = useRef(`sess-${Date.now()}`);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, streaming]);

    useEffect(() => {
        if (preset && open) {
            send(preset);
            onPresetConsumed?.();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preset, open]);

    const send = async (text, ctxType, ctxId) => {
        const message = (text ?? input).trim();
        if (!message || streaming) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: message }]);
        setMessages((m) => [...m, { role: "assistant", content: "" }]);
        setStreaming(true);
        try {
            const token = localStorage.getItem("apptrack_token");
            const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    message,
                    session_id: sessionId.current,
                    context_type: ctxType ?? contextType,
                    context_id: ctxId ?? contextId,
                }),
            });
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();
                for (const line of lines) {
                    if (!line.startsWith("data: ")) continue;
                    const payload = line.slice(6).trim();
                    if (payload === "[DONE]") continue;
                    try {
                        const parsed = JSON.parse(payload);
                        if (parsed.delta) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + parsed.delta };
                                return copy;
                            });
                        } else if (parsed.error) {
                            setMessages((m) => {
                                const copy = [...m];
                                copy[copy.length - 1] = { role: "assistant", content: `Error: ${parsed.error}` };
                                return copy;
                            });
                        }
                    } catch {}
                }
            }
        } catch (e) {
            setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: tr("Connection error. Please try again.") };
                return copy;
            });
        } finally {
            setStreaming(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.aside
                    data-testid="ai-chat-panel"
                    initial={{ x: 440, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 440, opacity: 0 }}
                    transition={{ type: "spring", damping: 30, stiffness: 300 }}
                    className="fixed right-0 top-0 h-screen w-full sm:w-[420px] z-50 glass shadow-2xl flex flex-col"
                >
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200/60">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-[8px] bg-teal flex items-center justify-center pulse-ring">
                                <Sparkles className="w-4 h-4 text-white" />
                            </div>
                            <div>
                                <div className="font-heading font-semibold text-sm text-navy dark:text-white">NexusOne AI</div>
                                <div className="text-[11px] text-slate-500 dark:text-slate-400">{tr("Agentic copilot · context-aware")}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <button data-testid="ai-clear-btn" onClick={() => setMessages([])} className="p-2 rounded-[6px] hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 transition-colors" title={tr("Clear conversation")}>
                                <Trash2 className="w-4 h-4" />
                            </button>
                            <button data-testid="ai-close-btn" onClick={onClose} className="p-2 rounded-[6px] hover:bg-slate-200/60 dark:hover:bg-white/10 text-slate-500 transition-colors">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                        {messages.length === 0 && (
                            <div className="mt-8 text-center">
                                <div className="w-14 h-14 mx-auto rounded-[12px] bg-teal/10 flex items-center justify-center mb-4">
                                    <Sparkles className="w-6 h-6 text-teal" />
                                </div>
                                <p className="font-heading font-semibold text-navy dark:text-white">{tr("How can I help?")}</p>
                                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-[280px] mx-auto">
                                    {tr("I can summarize circulations, check Planning Act timelines, draft reports, and query your data.")}
                                </p>
                                <div className="mt-6 flex flex-wrap gap-2 justify-center">
                                    {SUGGESTIONS.map((s) => (
                                        <button
                                            key={s.id}
                                            data-testid={`ai-suggestion-${s.id}`}
                                            onClick={() => send(s.prompt, s.id === "askdata" ? "data" : undefined)}
                                            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-slate-200 dark:border-white/15 bg-white/60 dark:bg-white/5 text-navy dark:text-slate-200 hover:border-[#00B4D8] hover:text-[#00B4D8] transition-colors"
                                        >
                                            <s.icon className="w-3.5 h-3.5" />
                                            {tr(s.label)}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {messages.map((m, i) => (
                            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div
                                    className={`max-w-[85%] rounded-[12px] px-4 py-2.5 text-sm ${
                                        m.role === "user"
                                            ? "bg-teal text-white rounded-br-[4px]"
                                            : "bg-white/80 dark:bg-white/10 text-navy dark:text-slate-100 border border-slate-200/60 dark:border-white/10 rounded-bl-[4px]"
                                    }`}
                                >
                                    {m.role === "assistant" ? (
                                        m.content ? (
                                            <div className="ai-markdown"><Markdown text={m.content} /></div>
                                        ) : (
                                            <div className="flex gap-1 py-1">
                                                <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                                                <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                                                <span className="typing-dot w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />
                                            </div>
                                        )
                                    ) : (
                                        m.content
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={bottomRef} />
                    </div>

                    <div className="p-4 border-t border-slate-200/60">
                        <div className="flex items-center gap-2">
                            <input
                                data-testid="ai-chat-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && send()}
                                placeholder={tr("Ask anything about this file…")}
                                className="flex-1 bg-white/80 dark:bg-white/10 border border-slate-200 dark:border-white/10 rounded-[8px] px-3.5 py-2.5 text-sm text-navy dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:ring-offset-1"
                            />
                            <button
                                data-testid="ai-send-btn"
                                onClick={() => send()}
                                disabled={streaming || !input.trim()}
                                className="brand-btn w-10 h-10 rounded-[8px] flex items-center justify-center disabled:opacity-40"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </motion.aside>
            )}
        </AnimatePresence>
    );
}
